import React, { useState, useEffect, useRef, useContext } from 'react';
import { toast } from '../../../../utils/toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Box,
  Typography,
  Alert,
  Grid,
  MenuItem,
  IconButton,
  styled
} from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { ClienteDto } from '../../../../services/api/clientesApi';
import { useMaintenanceApi } from '../../../../services/api/maintenanceApi';
import { GenericItem } from '../../../../components/types/generic.types';
import { DarkModeContext } from '../../../../context/darkModeContext';
import api from '../../../../utils/axios';

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialogTitle-root': {
    backgroundColor: '#7451f8',
    color: 'white',
    padding: '12px 20px',
  },
  '& .MuiDialogContent-root': {
    padding: '20px 24px',
  },
  '& .MuiDialogActions-root': {
    padding: '12px 24px',
  },
}));

const ImageUploadBox = styled(Box)(() => ({
  width: '100%',
  height: 190,
  border: '2px dashed #7451f8',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background 0.2s',
  '&:hover': { backgroundColor: '#7451f810' },
}));

const DeleteImageButton = styled(Button)(() => ({
  marginTop: 6,
  color: '#d32f2f',
  backgroundColor: 'rgba(211,47,47,0.08)',
  '&:hover': { backgroundColor: 'rgba(211,47,47,0.16)' },
}));

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

interface ClienteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ClienteDto) => Promise<void>;
  title: string;
  initialData?: ClienteDto;
  isLoading: boolean;
  isEdit?: boolean;
}

const EMPTY_FORM: ClienteDto = {
  IdTipoCliente: 0,
  IdTipoDocumento: 0,
  NumeroDocumento: '',
  NombreCompleto: '',
  FechaNacimiento: '',
  Telefono: '',
  Direccion: '',
  Correo: '',
  Imagen: '',
  Activo: true,
};

const SectionHeader: React.FC<{ label: string; dark: boolean }> = ({ label, dark }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1.5,
      mt: 0.5,
      pl: 1.5,
      py: 0.5,
      borderLeft: '3px solid #7451f8',
      bgcolor: dark ? 'rgba(116,81,248,0.08)' : 'rgba(116,81,248,0.05)',
      borderRadius: '0 6px 6px 0',
    }}
  >
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#7451f8', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
);

const ClienteModal: React.FC<ClienteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialData,
  isLoading,
  isEdit = false,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const [formData, setFormData] = useState<ClienteDto>(EMPTY_FORM);
  const [deleteImage, setDeleteImage] = useState(false);
  const [tiposDocumento, setTiposDocumento] = useState<GenericItem[]>([]);
  const [tiposCliente, setTiposCliente] = useState<GenericItem[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tipoDocumentoApi = useMaintenanceApi('tipos-documento');
  const tipoClienteApi = useMaintenanceApi('tipos-cliente');

  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        const [tdocs, tclientes] = await Promise.all([
          tipoDocumentoApi.getAll(),
          tipoClienteApi.getAll(),
        ]);
        setTiposDocumento(tdocs.filter(t => t.Activo));
        setTiposCliente(tclientes.filter(t => t.Activo));
      } catch (error) {
        console.error('Error loading catalogs:', error);
      }
    };
    loadCatalogos();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.Imagen) {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4020';
        setImagePreview(`${baseUrl}${initialData.Imagen}`);
      } else {
        setImagePreview('');
      }
    } else {
      setFormData(EMPTY_FORM);
      setImagePreview('');
    }
    setFileError('');
    setEmailError('');
  }, [initialData, isOpen]);

  const handleDeleteImage = () => {
    setImagePreview('');
    setDeleteImage(true);
    setFormData(prev => ({ ...prev, Imagen: '', deleteImage: true }));
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileError('');
    setDeleteImage(false);
    if (!file) return;
    if (!ALLOWED_FILE_TYPES.includes(file.type)) { setFileError('El archivo debe ser una imagen JPG o PNG'); return; }
    if (file.size > MAX_FILE_SIZE) { setFileError('El archivo no debe superar los 20MB'); return; }
    try {
      const fd = new FormData();
      fd.append('imagen', file);
      const response = await api.post('/administracion/clientes/upload-imagen', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4020';
      setImagePreview(`${baseUrl}${response.data.url}`);
      setFormData(prev => ({ ...prev, Imagen: response.data.url }));
    } catch {
      setFileError('Error al subir la imagen');
    }
  };

  const validateEmail = (email: string) => {
    if (email && !EMAIL_REGEX.test(email)) {
      setEmailError('Formato de correo electrónico inválido');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(formData.Correo) || fileError) return;
    try {
      await onSubmit(formData);
    } catch (err: any) {
      toast.error('Error');
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: darkMode ? '#2b2b2b' : '#f7f7f5',
      borderRadius: '8px',
      '&.Mui-focused fieldset': { borderColor: '#7451f8' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#7451f8' },
  };

  return (
    <StyledDialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon fontSize="small" />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{title}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ bgcolor: darkMode ? '#1a1a1a' : '#fff' }}>
          <Grid container spacing={2.5}>
            {/* Columna imagen */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  id="cliente-imagen-input"
                  ref={fileInputRef}
                />
                <ImageUploadBox
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ border: fileError ? '2px dashed #d32f2f' : '2px dashed #7451f8', bgcolor: darkMode ? '#2b2b2b' : undefined }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Vista previa"
                      style={{ maxWidth: '90%', maxHeight: '155px', borderRadius: '6px', objectFit: 'contain' }}
                    />
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 36, color: '#7451f8', mb: 0.5 }} />
                      <Typography variant="caption" color="textSecondary">
                        {deleteImage ? 'Imagen eliminada' : 'Click para subir imagen'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">JPG, PNG (máx. 20MB)</Typography>
                    </>
                  )}
                </ImageUploadBox>
                {imagePreview && (
                  <DeleteImageButton startIcon={<DeleteSweepIcon />} onClick={handleDeleteImage} size="small" fullWidth>
                    Eliminar imagen
                  </DeleteImageButton>
                )}
                {fileError && <Alert severity="error" sx={{ mt: 1, fontSize: '0.78rem' }}>{fileError}</Alert>}
              </Box>
            </Grid>

            {/* Columna datos */}
            <Grid item xs={12} md={8}>
              <SectionHeader label="Información Personal" dark={darkMode} />
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select size="small" fullWidth label="Tipo de Cliente" required sx={fieldSx}
                    value={formData.IdTipoCliente}
                    onChange={e => setFormData({ ...formData, IdTipoCliente: Number(e.target.value) })}
                  >
                    <MenuItem value={0} disabled>Seleccione un tipo</MenuItem>
                    {tiposCliente.map(t => <MenuItem key={t.Id} value={t.Id}>{t.Nombre}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select size="small" fullWidth label="Tipo de Documento" required sx={fieldSx}
                    value={formData.IdTipoDocumento}
                    onChange={e => setFormData({ ...formData, IdTipoDocumento: Number(e.target.value) })}
                  >
                    <MenuItem value={0} disabled>Seleccione un tipo</MenuItem>
                    {tiposDocumento.map(t => <MenuItem key={t.Id} value={t.Id}>{t.Nombre}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small" fullWidth label="Número de Documento" required sx={fieldSx}
                    value={formData.NumeroDocumento}
                    onChange={e => setFormData({ ...formData, NumeroDocumento: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small" fullWidth label="Nombre Completo" required sx={fieldSx}
                    value={formData.NombreCompleto}
                    onChange={e => setFormData({ ...formData, NombreCompleto: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small" fullWidth label="Fecha de Nacimiento" type="date" required sx={fieldSx}
                    value={formData.FechaNacimiento}
                    onChange={e => setFormData({ ...formData, FechaNacimiento: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, mb: 0.5 }}>
                <SectionHeader label="Información de Contacto" dark={darkMode} />
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small" fullWidth label="Teléfono" sx={fieldSx}
                    value={formData.Telefono}
                    onChange={e => setFormData({ ...formData, Telefono: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small" fullWidth label="Correo Electrónico" type="email" sx={fieldSx}
                    value={formData.Correo}
                    onChange={e => { setFormData({ ...formData, Correo: e.target.value }); validateEmail(e.target.value); }}
                    error={!!emailError}
                    helperText={emailError}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small" fullWidth label="Dirección" sx={fieldSx}
                    value={formData.Direccion}
                    onChange={e => setFormData({ ...formData, Direccion: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Activo switch */}
            {!isEdit && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Activo}
                      onChange={e => setFormData({ ...formData, Activo: e.target.checked })}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#7451f8' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#7451f8' } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.88rem' }}>Activo</Typography>}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ bgcolor: darkMode ? '#1a1a1a' : '#fff', borderTop: `1px solid ${darkMode ? '#333' : '#e9e9e7'}` }}>
          <Button
            onClick={onClose} disabled={isLoading}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: darkMode ? '#ccc' : '#555' }}
          >
            Cancelar
          </Button>
          <Button
            type="submit" variant="contained" disabled={isLoading || !!fileError || !!emailError}
            disableElevation
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, bgcolor: '#7451f8', '&:hover': { bgcolor: '#5e3cc4' }, minWidth: 110 }}
          >
            {isLoading ? 'Guardando…' : isEdit ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default ClienteModal;
